import type { ImageProps } from "next/image";
import type { ReactNode } from "react";

import { Container, Grid } from "@radix-ui/themes";
import clsx from "clsx";
import Image from "next/image";

import type { Link } from "@/src/domain/model/EditorialPage";

import { robotoCondensed } from "@/fonts";
import { ButtonLink } from "@/src/components/ui/Button/ButtonLink";

interface HeadingBannerProps {
  readonly image?: ImageProps | null;
  readonly link?: Link | null;
  readonly subtitle?: null | ReactNode;
  readonly tag?: null | string;
  readonly title: ReactNode | string;
}

export function HeadingBanner({
  image,
  link,
  subtitle,
  tag,
  title,
}: HeadingBannerProps): ReactNode {
  return (
    <Container asChild size="4">
      <header
        className={clsx(
          "bg-primary rounded-br-3xl px-4 py-8 text-white",
          "md:py-12",
        )}
      >
        <Grid
          align="center"
          columns={{ initial: "1", lg: "2fr 1fr" }}
          gap={{ initial: "4", lg: "8" }}
        >
          <div>
            {tag ? (
              <p className="font-bold-antialiased bg-primary-darkest bubble-xl w-fit px-2 py-1 text-sm uppercase">
                {tag}
              </p>
            ) : null}
            <h1
              className={clsx(
                robotoCondensed.className,
                "font-bold-antialiased text-4xl uppercase leading-tight",
                "md:text-5xl md:leading-tight",
              )}
            >
              {title}
            </h1>
          </div>
          <div className={clsx({ "col-start-1": image })}>
            {subtitle}
            {link ? (
              <ButtonLink
                className="mt-6 block"
                darkBackground
                href={link.url}
                size="big"
              >
                {link.text}
              </ButtonLink>
            ) : null}
          </div>
          {image ? (
            <div
              className={clsx(
                "bubble-3xl relative col-start-2 row-span-2 row-start-1 hidden h-full w-[350px] justify-self-end overflow-hidden",
                "md:block",
              )}
            >
              <Image
                alt={image.alt}
                className="object-cover"
                fill
                priority
                sizes="(max-width: 768px) 0vw, (max-width: 1200px) 30vw"
                src={image.src}
              />
            </div>
          ) : null}
        </Grid>
      </header>
    </Container>
  );
}
